/**
 * Phase 20 — the editor's Auto-send tab: on a master config its campaign
 * group's switch, daily cap and stop-loss; on a container "Use master config
 * | Own settings" (its own layer only adds on top — its switch can only take
 * it out). Four steps — Sending (Off · Simulated · Live — Simulated decides
 * like a send and sends nothing) · Daily cap · Stop-loss (with its replay over
 * the last days) · Right now (the rule being edited, judged on today) — saved
 * with the editor's Save; switching a group ON for real asks once more at Save.
 * A master config shows which containers keep their own layer (Override N).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Activity, Gauge, Send } from 'lucide-react';
import { type DropGenAutoSendConfig, type DropGenAutoSendMode, haltedOn, stopLossOn } from '@trafficpulse/shared';

import { SegmentedPills, StepCard, StepCardGroup } from '@/components/ui';
import { useAuth } from '@/providers/AuthProvider';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import type { DraftAutoSend } from '../../../drop-generator-rule.types';
import { useSchedulerStatus } from '../../../useDropGenerator';
import { MODE_LINE } from '../../board-header/autosend/autosend-fact';
import { CapField } from '../../board-header/autosend/settings/CapField';
import { CONTAINER_MODE_OPTIONS, liveKind, modeOptions, useModeRights } from '../../board-header/autosend/groups/mode-options';
import { OwnersLine } from '../master/push/markers/OwnersLine';
import { useSectionSources } from '../master/section-sources';
import type { ContainerEditor } from '../state/useContainerEditor';
import { draftConfig, ownsAutoSend, switchesGroupOn } from './autosend-slice';
import { LayerSummary } from './LayerSummary';
import { AutoSendStory } from './story/AutoSendStory';
import { RightNowCard, rightNowSummary } from './RightNowCard';
import { StopLossStep } from './stop-loss/StopLossStep';
import { useStopLossPreview } from './use-stop-loss-preview';

const SOURCE = [
  { value: 'master', label: 'Use master config' },
  { value: 'own', label: 'Own settings', tone: 'warning' },
] as const;

export const AutoSendTab: FC<{
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
}> = ({ editor, plan, containerId }) => {
  const { draft } = editor;
  const sources = useSectionSources();
  const isMaster = draft.kind === 'MASTER';
  const groupName = isMaster ? draft.name || 'this campaign group' : (sources.masterRef?.name ?? 'its campaign group');
  const own = ownsAutoSend(draft);
  const setOwn = (next: boolean) => {
    editor.setSectionSource('autoSend', next, null);
    // Turning its own layer on keeps it sending — only its switch takes it out.
    if (next && draft.autoSendBase.autoSend === false && !draft.ownSections.autoSend)
      editor.setField('autoSend', { ...draft.autoSend, enabled: true });
  };
  return (
    <div className="flex flex-col gap-3">
      {isMaster ? (
        <OwnersLine section="autoSend" />
      ) : (
        <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/20 p-3 text-xs">
          {sources.masterRef ? (
            <LayerSummary title={`Campaign group ${groupName}`} config={sources.masterRef.autoSend} kind="group" groupName={groupName} />
          ) : null}
          <SegmentedPills options={SOURCE} value={own ? 'own' : 'master'} onChange={(v) => setOwn(v === 'own')} />
          {own ? null : (
            <p className="text-muted-foreground">
              {draft.name || 'This container'} sends exactly as {groupName} does.
            </p>
          )}
        </div>
      )}
      {isMaster || own ? <Steps editor={editor} plan={plan} containerId={containerId} groupName={groupName} /> : null}
    </div>
  );
};

const Steps: FC<{
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
  groupName: string;
}> = ({ editor, plan, containerId, groupName }) => {
  const { hasPermission } = useAuth();
  const { draft } = editor;
  const isMaster = draft.kind === 'MASTER';
  const d = draft.autoSend;
  const patch = (p: Partial<DraftAutoSend>) => editor.setField('autoSend', { ...d, ...p });
  const status = useSchedulerStatus().data;
  const mine = status?.plans.find((p) => p.countryKey === plan.countryKey);
  const config = draftConfig(draft);
  const preview = useStopLossPreview(containerId, config);
  const whose = isMaster ? groupName : draft.name || 'this container';
  const sentSms = isMaster ? (mine?.groups.find((g) => g.masterId === containerId)?.sendToday?.sentSms ?? null) : null;
  const canEdit = hasPermission('drop_generator.autosend');
  const rights = useModeRights();
  const mode: DropGenAutoSendMode = !d.enabled ? 'off' : d.shadow ? 'shadow' : 'on';
  // 20/10/2026 — a group is Off · Simulated · Live (by permission and server); a container's "On" = on while its group is.
  const options = isMaster ? modeOptions(rights, mode) : CONTAINER_MODE_OPTIONS;
  const setMode = (m: DropGenAutoSendMode) => patch({ enabled: m !== 'off', shadow: m === 'shadow' });
  return (
    <StepCardGroup>
      <StepCard index={1} icon={Send} title="Sending" summary={sendingWords(mode, isMaster, groupName)}>
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className={canEdit ? '' : 'pointer-events-none opacity-50'} aria-label={`Auto-send for ${whose}`}>
              <SegmentedPills options={options} value={mode} onChange={setMode} />
            </span>
            <span className="text-muted-foreground">— {sendingWords(mode, isMaster, groupName)}</span>
          </div>
          {isMaster ? (
            <div className="my-2 rounded-lg border border-border/60 bg-muted/20 p-3">
              <p className="mb-2 text-xs font-semibold">Delivery timing</p>
              <SegmentedPills
                label="Delivery timing"
                options={[
                  { value: 'DUE_TIME', label: 'Keep local until due' },
                  { value: 'SCHEDULE_AHEAD', label: 'Schedule in SMSWiser' },
                ]}
                value={d.dispatchMode ?? 'DUE_TIME'}
                onChange={(dispatchMode) => patch({ dispatchMode })}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                {d.dispatchMode === 'SCHEDULE_AHEAD'
                  ? 'Ready drops are submitted with their scheduled times, up to 24 hours ahead. Saving updates an existing generated day. Rules can update or cancel pending drops. SMSWiser keeps the schedule running while TrafficPulse is offline.'
                  : 'Drafts stay editable locally. The engine submits each drop when its scheduled time arrives, using the latest settings and performance decisions.'}
              </p>
            </div>
          ) : null}
          <AutoSendStory mode={mode} whose={whose} cap={d.noCap ? null : d.cap} hasStopLoss={d.stopLossEnabled && (d.rows.length > 0 || d.advanced)} />
          {mode === 'shadow' ? (
            <p className="text-xs text-warning">Switch to Live when you trust what the feed says it would have sent.</p>
          ) : null}
          {isMaster && mode === 'on' ? <p className="text-[11px] text-muted-foreground">{MODE_LINE[liveKind(rights.server)]}</p> : null}
          {isMaster && mode === 'on' && switchesGroupOn(draft) ? (
            <p className="text-xs font-medium text-warning">Save asks once more before {groupName} sends by itself.</p>
          ) : null}
          <HaltNote config={liveLayer(plan, containerId, isMaster)} runDate={mine?.runDate ?? null} />
        </div>
      </StepCard>
      <StepCard
        index={2}
        icon={Gauge}
        title="Daily cap"
        summary={
          d.noCap ? (isMaster ? 'no limit' : `none of its own — ${groupName}’s applies`) : `${d.cap.toLocaleString('en-US')} SMS a day`
        }
      >
        <p className="mb-2 text-xs text-muted-foreground">
          {isMaster
            ? `Every SMS sent today from ${groupName}’s drafts — yours and the engine’s.`
            : `${whose} only — they count toward ${groupName}’s cap too.`}
        </p>
        <CapField
          noCap={d.noCap}
          cap={d.cap}
          sentSms={sentSms}
          holdNote={isMaster ? `held — ${whose}'s daily cap is reached` : `held — ${whose}'s own daily cap is reached`}
          onChange={patch}
        />
      </StepCard>
      <StopLossStep editor={editor} plan={plan} containerId={containerId} config={config} patch={patch} groupName={groupName} whose={whose} />
      <StepCard
        index={4}
        icon={Activity}
        title="Right now"
        summary={containerId && stopLossOn(config) ? rightNowSummary(preview.data) : 'not judged yet'}
      >
        <RightNowCard
          saved={containerId !== null}
          hasRule={stopLossOn(config)}
          whose={whose}
          tz={plan.recipientTimezone}
          query={preview}
        />
      </StepCard>
    </StepCardGroup>
  );
};

const sendingWords = (mode: DropGenAutoSendMode, isMaster: boolean, groupName: string) => {
  if (mode === 'shadow')
    return isMaster
      ? `simulated — on the real containers ${groupName} says what it would have sent, nothing goes out`
      : `simulated — this container says what it would have sent, nothing goes out`;
  return isMaster
    ? mode === 'on'
      ? `every container of ${groupName} sends its drafts at their planned time — real SMS`
      : 'you send'
    : mode === 'on'
      ? `on while ${groupName} is on`
      : `this container waits for a person — the rest of ${groupName} still sends`;
};

/** The layer as the board has it now (it refreshes after a Resume; the draft does not). */
const liveLayer = (plan: DropGenPlanDetail, id: string | null, isMaster: boolean): DropGenAutoSendConfig | null =>
  isMaster ? (plan.masters.find((m) => m.id === id)?.autoSend ?? null) : (plan.containers.find((c) => c.id === id)?.ownAutoSend ?? null);

/** A stop-loss halt in force today — Resume lives with the halt banner and the Groups table. */
const HaltNote: FC<{
  config: DropGenAutoSendConfig | null;
  runDate: string | null;
}> = ({ config, runDate }) =>
  config && runDate && haltedOn(config, runDate) ? (
    <p className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-xs text-warning">
      Halted for today — {config.haltReason}. Resume it from the banner on the country page.
    </p>
  ) : null;
